/**
 * Preset Scoring Configurations
 * 
 * These are pre-defined configurations that hosts can choose from
 * Each represents a different matchmaking philosophy
 */

import { DEFAULT_SCORING_CONFIG } from './normalizedScoring.js';

/**
 * Available preset configurations
 */
export const SCORING_PRESETS = {
  /**
   * Default/Balanced - Good for most recreational sessions
   * Prioritizes fairness and competitive balance while encouraging variety
   */
  default: DEFAULT_SCORING_CONFIG,
  
  /**
   * Competitive - For tournament-style or advanced sessions
   * Maximizes competitive balance, deprioritizes fairness slightly
   */
  competitive: {
    weights: {
      fairness: 0.25,      // Less focus on equal game count
      eloBalance: 0.45,    // VERY important - close matches
      interactions: 0.15,  // OK with repeats if they're competitive
      gameType: 0.15,
    },
    description: 'Competitive - Prioritizes close, balanced matches over equal game counts',
    bestFor: 'Advanced players, tournaments, skill-focused sessions',
  },
  
  /**
   * Social - For casual/beginner sessions
   * Maximizes fairness and variety, less concerned with ELO balance
   */
  social: {
    weights: {
      fairness: 0.50,      // Very important - everyone gets equal games
      eloBalance: 0.10,    // Less important - mix skill levels
      interactions: 0.25,  // Important - meet different people
      gameType: 0.15,
    },
    description: 'Social - Ensures everyone plays equally, maximizes variety',
    bestFor: 'Beginner sessions, social meetups, mixed skill levels',
  },
  
  /**
   * Variety-Focused - Minimize repeat interactions
   * Great for large clubs where players want to meet new people
   */
  variety: {
    weights: {
      fairness: 0.30,
      eloBalance: 0.20,
      interactions: 0.35,  // Maximum variety
      gameType: 0.15,
    },
    description: 'Variety - Maximizes fresh matchups and new partnerships',
    bestFor: 'Large clubs, networking events, variety-seeking players',
  },
  
  /**
   * Game-Type-First - Prioritise balanced matchups and game variety
   * Category gap still enforced as hard block when categories enabled
   */
  categoryStrict: {
    weights: {
      fairness: 0.30,
      eloBalance: 0.25,
      interactions: 0.15,
      gameType: 0.30,      // High weight on game type / variety
    },
    description: 'Game-Type-First - Prioritises balanced matchups and variety (category gap = hard block only)',
    bestFor: 'Sessions where game type balance and variety matter most',
  },
  
  /**
   * Fairness-First - Absolute priority on equal game counts
   * Good for limited-time sessions where everyone must play equally
   */
  fairnessFirst: {
    weights: {
      fairness: 0.60,      // Dominant priority
      eloBalance: 0.15,
      interactions: 0.15,
      gameType: 0.10,
    },
    description: 'Fairness-First - Guarantees equal playing time above all else',
    bestFor: 'Time-limited sessions, drop-in play, fairness-critical events',
  },
};

/**
 * Get preset configuration by key
 * @param {string} presetKey - Key from SCORING_PRESETS
 * @returns {Object} Configuration object
 */
export function getPresetConfig(presetKey) {
  if (!SCORING_PRESETS[presetKey]) {
    console.warn(`Unknown preset "${presetKey}", falling back to default`);
    return SCORING_PRESETS.default;
  }
  return SCORING_PRESETS[presetKey];
}

/**
 * Get list of available presets with metadata
 * Useful for building a UI selector
 */
export function getAvailablePresets() {
  return Object.entries(SCORING_PRESETS).map(([key, config]) => ({
    key,
    name: key.charAt(0).toUpperCase() + key.slice(1),
    description: config.description || 'Default configuration',
    bestFor: config.bestFor || 'General use',
    weights: config.weights,
  }));
}

/**
 * Example of how to use custom weights (for future host configuration)
 * 
 * @example
 * // Host creates custom config via UI
 * const hostCustomConfig = {
 *   weights: {
 *     fairness: 0.40,
 *     eloBalance: 0.30,
 *     interactions: 0.15,
 *     gameType: 0.15,
 *   },
 *   customName: "My Club's Special Config",
 * };
 * 
 * // Save to database
 * await supabase
 *   .from('sessions')
 *   .update({ 
 *     scoring_config: JSON.stringify(hostCustomConfig)
 *   })
 *   .eq('id', sessionId);
 * 
 * // Load and use
 * const session = await getSession(sessionId);
 * const config = session.scoring_config 
 *   ? JSON.parse(session.scoring_config)
 *   : DEFAULT_SCORING_CONFIG;
 * 
 * const score = calculateFinalScore(dimensionScores, config);
 */
